import { Size, Sizes } from './types';

export const MAX_GENERATED_SIZE_COUNT = 2000000;

interface ItemMetadataArgs {
  index: number;
  sizes: Sizes;
}

interface EstimatedTotalSizeArgs {
  itemCount: number;
  sizes: Sizes;
}

interface GenerateSizesArgs {
  itemCount: number;
  sizes?: Sizes;
  estimatedItemSize: number;
}

interface StartIndexArgs {
  itemCount: number;
  scrollOffset: number;
  sizes: Sizes;
}

interface StopIndexArgs extends StartIndexArgs {
  startIndex: number;
  height: number;
}

/**
 * Calculate the start index for the current offset in the list
 */
export const getStartIndexForOffset = ({ itemCount, scrollOffset, sizes }: StartIndexArgs) => {
  let totalHeight;
  let a = 0;
  let b = itemCount - 1;
  // eslint-disable-next-line no-bitwise
  let i = ~~(itemCount / 2);
  let oldI;

  // Searching for startIndex
  while (i !== oldI) {
    oldI = i;
    totalHeight = sizes.getAccumulator(i);
    if (totalHeight < scrollOffset) {
      a = i;
    } else if (i < itemCount - 1 && sizes.getAccumulator(i + 1) > scrollOffset) {
      b = i;
    }
    // eslint-disable-next-line no-bitwise
    i = ~~((a + b) / 2);
  }

  if (i < 0) {
    i = 0;
  }
  const startIndex = i;

  return startIndex;
};

/**
 * Calculate the stop index for the current offset in the list
 */
export const getStopIndexForStartIndex = ({
  itemCount,
  scrollOffset,
  sizes,
  startIndex,
  height,
}: StopIndexArgs) => {
  let endIndex;
  const maxOffset = scrollOffset + height;
  // Searching for endIndex
  for (
    endIndex = startIndex;
    endIndex < itemCount && sizes.getAccumulator(endIndex) < maxOffset;
    endIndex += 1
  );
  if (endIndex === -1) {
    endIndex = itemCount - 1;
  } else {
    endIndex += 1;
    // Bounds
    if (endIndex > itemCount) {
      endIndex = itemCount;
    }
  }

  return endIndex;
};

const ACCUMULATOR_INDEX = 0;
const SIZE_INDEX = 1;

/**
 * Generate sizes and offsets for items
 */
export const generateSizes = ({ itemCount, sizes, estimatedItemSize }: GenerateSizesArgs) => {
  const sizesSize = Math.min(MAX_GENERATED_SIZE_COUNT, itemCount);
  const sizesArr = new Array<Size>(sizesSize);

  const getCount = () => itemCount;

  const getSize = (i: number) => {
    if (i < MAX_GENERATED_SIZE_COUNT) {
      return sizesArr[i]?.[SIZE_INDEX];
    }

    return estimatedItemSize;
  };

  const getEffectiveSize = (i: number) => getSize(i) ?? estimatedItemSize;

  const getAccumulator = (index: number) => {
    if (index < MAX_GENERATED_SIZE_COUNT) {
      return sizesArr[index]?.[ACCUMULATOR_INDEX] ?? 0;
    }

    const numSkippedItems = index - (MAX_GENERATED_SIZE_COUNT - 1);
    const finalAccumulator = sizesArr[MAX_GENERATED_SIZE_COUNT - 1][0];

    if (index < itemCount) {
      return finalAccumulator + numSkippedItems * estimatedItemSize;
    }

    return 0;
  };

  const setSize = (index: number, size: number) => {
    if (!sizesArr[index]) {
      sizesArr[index] = [getAccumulator(index), size];
    } else {
      sizesArr[index][SIZE_INDEX] = size;
    }
  };

  let accumulator = 0;
  let current;
  for (let i = 0, l = Math.min(MAX_GENERATED_SIZE_COUNT, itemCount); i < l; i += 1) {
    current = sizes?.getSize(i);
    accumulator += current ?? estimatedItemSize;
    sizesArr[i] = [accumulator, current];
  }

  const newSizes = {
    getAccumulator,
    getCount,
    getEffectiveSize,
    getSize,
    setSize,
  };

  return newSizes;
};

export const getItemOffset = ({ index, sizes }: ItemMetadataArgs): number =>
  sizes.getAccumulator(index - 1); // get the height up to the previous element

export const getItemSize = ({ index, sizes }: ItemMetadataArgs): number =>
  sizes.getEffectiveSize(index);

export const getEstimatedTotalSize = ({ itemCount, sizes }: EstimatedTotalSizeArgs) =>
  sizes.getAccumulator(itemCount - 1);
